import { Fragment, useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { Button, Input, ScrollShadow, InputErrorMsg } from "components/ui";
import { toast } from "sonner";
import { createContact } from "services/contactServices";
import { autocompleteContact } from "services/formBuilderService";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import { cleanPhoneNumber, isPhoneEmpty, isPhoneEmptyOrValid } from "../../../../../../utils/phoneUtils";
import "react-phone-number-input/style.css";
import clsx from "clsx";
import CustomCreatableDropdown from "custom-components/shared/CustomCreatableDropdown";
import { useDropdown, useSuburbRecommendation } from "hooks";
import { DropdownEnum } from "enum/DropdownEnum";
import { formatABN } from "utils/formatABN";

export function AddContactModal({
  show,
  onClose,
  onAdd,
  leadId = null,
  jobId = null,
}) {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("+61");
  const [streetAddress, setStreetAddress] = useState("");
  const [suburb, setSuburb] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [state, setState] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [abnNumber, setAbnNumber] = useState("");

  const [suggestions, setSuggestions] = useState({});
  const [showSuggestions, setShowSuggestions] = useState({});
  const [searchingField, setSearchingField] = useState({});
  const searchTimeoutRef = useRef({});

  const handleSearchContact = (fieldName, value, searchType) => {
    if (searchTimeoutRef.current[fieldName]) {
      clearTimeout(searchTimeoutRef.current[fieldName]);
    }

    const trimmed = (value || "").trim();
    if (trimmed.length < 4) {
      setSuggestions((prev) => ({ ...prev, [fieldName]: [] }));
      setShowSuggestions((prev) => ({ ...prev, [fieldName]: false }));
      setSearchingField((prev) => ({ ...prev, [fieldName]: false }));
      return;
    }

    setSearchingField((prev) => ({ ...prev, [fieldName]: true }));
    setShowSuggestions((prev) => ({ ...prev, [fieldName]: true }));

    searchTimeoutRef.current[fieldName] = setTimeout(async () => {
      try {
        const response = await autocompleteContact(trimmed, searchType);
        if (response.success && response.data && response.data.length > 0) {
          setSuggestions((prev) => ({ ...prev, [fieldName]: response.data }));
          setShowSuggestions((prev) => ({ ...prev, [fieldName]: true }));
        } else {
          setShowSuggestions((prev) => ({ ...prev, [fieldName]: false }));
        }
      } catch (err) {
        console.error("Autocomplete error:", err);
        setShowSuggestions((prev) => ({ ...prev, [fieldName]: false }));
      } finally {
        setSearchingField((prev) => ({ ...prev, [fieldName]: false }));
      }
    }, 400);
  };

  const handleSelectContactSuggestion = (fieldName, contact) => {
    if (contact.fullName) {
      setFullName(contact.fullName);
      setErrors((prev) => ({ ...prev, fullName: "" }));
    }
    if (contact.email) {
      setEmail(contact.email);
      setErrors((prev) => ({ ...prev, email: "" }));
    }
    if (contact.phoneNumber) {
      let p = contact.phoneNumber;
      if (!p.startsWith("+") && !p.startsWith("0")) {
        p = `+61${p}`;
      }
      if (p.startsWith("+61")) {
        const digits = p.slice(3).replace(/\D/g, "");
        if (digits.length > 10) p = `+61${digits.slice(0, 10)}`;
      } else {
        const digits = p.replace(/\D/g, "");
        if (digits.length > 10) p = digits.slice(0, 10);
      }
      setPhoneNumber(p);
      setErrors((prev) => ({ ...prev, phoneNumber: "" }));
    }
    if (contact.streetAddress) setStreetAddress(contact.streetAddress);
    if (contact.suburb) setSuburb(contact.suburb);
    if (contact.postalCode || contact.postcode) setPostalCode(contact.postalCode || contact.postcode);
    if (contact.state) setState(contact.state);
    if (contact.companyName || contact.company_name)
      setCompanyName(contact.companyName || contact.company_name);
    if (contact.abnNumber || contact.abn || contact.Abn)
      setAbnNumber(formatABN(contact.abnNumber || contact.abn || contact.Abn));

    setShowSuggestions({ fullName: false, email: false, phoneNumber: false });
  };

  const renderSuggestionsDropdown = (fieldName) => {
    const isSearching = searchingField[fieldName];
    const hasSuggestions =
      suggestions[fieldName] && suggestions[fieldName].length > 0;
    const isOpen = showSuggestions[fieldName];

    if (!isSearching && !hasSuggestions) return null;

    return (
      <div
        className={`dark:bg-dark-700 dark:border-dark-600 dark:divide-dark-600 absolute top-full right-0 left-0 z-[80] mt-1 max-h-60 origin-top transform divide-y divide-slate-100 overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-xl transition-all duration-200 ease-in-out ${isOpen ? "pointer-events-auto translate-y-0 scale-100 opacity-100" : "pointer-events-none -translate-y-2 scale-95 opacity-0"}`}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        {isSearching ? (
          <div className="flex items-center justify-center gap-2.5 px-4 py-4 text-sm text-slate-500 dark:text-slate-400">
            <svg
              className="text-primary h-4 w-4 animate-spin"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
              />
            </svg>
            <span>Searching contacts...</span>
          </div>
        ) : (
          suggestions[fieldName].map((contact) => (
            <div
              key={contact.contactId || contact.id}
              className="hover:bg-primary/5 dark:hover:bg-primary/10 cursor-pointer p-3 text-left transition-colors"
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleSelectContactSuggestion(fieldName, contact);
              }}
            >
              <div className="truncate text-sm font-semibold text-slate-800 dark:text-white">
                {contact.fullName}
              </div>
              <div className="mt-1 flex flex-col gap-0.5 text-xs text-slate-500 dark:text-slate-400">
                {contact.phoneNumber && (
                  <div className="flex items-center gap-1.5">
                    <span className="text-slate-400">📱</span>
                    <span>{contact.phoneNumber}</span>
                  </div>
                )}
                {contact.email && (
                  <div className="flex items-center gap-1.5">
                    <span className="text-slate-400">📧</span>
                    <span className="truncate">{contact.email}</span>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    );
  };

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const companyDropdown = useDropdown(DropdownEnum.Company, show);
  const suburbDropdown = useDropdown(DropdownEnum.Suburb, show);
  const stateDropdown = useDropdown(DropdownEnum.State, show);

  // Autofill State + PostalCode when the selected/typed Suburb matches a known
  // recommendation. Never fires for State-only entry — only Suburb resolves State/PostalCode.
  const lookupSuburbRecommendation = useSuburbRecommendation((rec) => {
    setState(rec.state || "");
    setPostalCode(rec.postalCode || "");
  });

  useEffect(() => {
    if (show) {
      setFullName("");
      setEmail("");
      setPhoneNumber("+61");
      setStreetAddress("");
      setSuburb("");
      setPostalCode("");
      setState("");
      setCompanyName("");
      setAbnNumber("");
      setErrors({});
    }
  }, [show]);
  const handleSave = async () => {
    if (!fullName.trim()) {
      setErrors({ fullName: "Full name is required" });
      toast.error("Full name is required");
      return;
    }

    const newErrors = {};
    if (email.trim() && !/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = "Invalid email format";
    }

    const cleanPhone = cleanPhoneNumber(phoneNumber);
    const hasPhone = !isPhoneEmpty(phoneNumber);
    const hasEmail = email && email.trim();

    if (!hasPhone && !hasEmail) {
      setErrors({ phoneNumber: true, email: true });
      toast.error("At least email or phone number is required");
      return;
    }

    try {
      if (hasPhone && !isPhoneEmptyOrValid(phoneNumber)) {
        newErrors.phoneNumber = "Please enter a valid phone number";
        toast.error("Please enter a valid phone number");
      }
    } catch (e) {
      if (hasPhone) {
        const cleanNum = phoneNumber.replace(/\D/g, "");
        if (cleanNum.length < 8 || cleanNum.length > 15) {
          newErrors.phoneNumber = "Please enter a valid phone number";
          toast.error("Please enter a valid phone number");
        }
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors((prev) => ({ ...prev, ...newErrors }));
      return;
    }

    setSubmitting(true);
    try {
      const cleanAbn = abnNumber ? abnNumber.replace(/\D/g, "") : "";
      const payload = {
        fullName: fullName.trim(),
        email: email.trim(),
        phoneNumber: cleanPhone,
        streetAddress: streetAddress.trim() || null,
        suburb: suburb.trim() || null,
        postalCode: postalCode.trim() || null,
        city: null,
        state: state.trim() || null,
        companyName: companyName.trim() || null,
        abnNumber: cleanAbn ? Number(cleanAbn) : null,
      };

      payload.jobId = jobId || null;
      payload.leadId = leadId || null;

      const res = await createContact(payload);
      if (res.success) {
        toast.success("Contact created successfully");
        let contactData = res.data;
        if (typeof contactData === "string") {
          try {
            contactData = JSON.parse(contactData);
          } catch (e) {
            console.warn("Failed to parse contactData in AddContactModal:", e);
          }
        }
        if (Array.isArray(contactData) && contactData.length > 0) {
          contactData = contactData[0];
        }
        
        let contactIdFromData = null;
        if (typeof contactData === "string" || typeof contactData === "number") {
          contactIdFromData = contactData;
        } else if (typeof contactData === "object" && contactData) {
          contactIdFromData = contactData.ContactId || contactData.id || contactData.Id;
        }

        const finalContact = {
          ...payload,
          id: contactIdFromData,
          ...(typeof contactData === "object" && contactData ? contactData : {})
        };

        onAdd?.(finalContact);
        onClose();
      } else {
        toast.error(res.error || "Failed to create contact");
      }
    } catch (err) {
      console.error("Error creating contact:", err);
      toast.error("An error occurred while creating contact");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Transition show={show} as={Fragment}>
      <Dialog as="div" className="relative z-[400]" onClose={() => {}}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm transition-opacity dark:bg-black/60" />
        </TransitionChild>

        <div className="fixed inset-0 z-10 overflow-hidden text-left">
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <DialogPanel className="dark:bg-dark-750 dark:border-dark-600 relative isolate flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-slate-100 bg-white text-left shadow-2xl transition-all">
                {/* Header */}
                <div className="dark:border-dark-600 flex items-center justify-between border-b border-slate-100 bg-slate-50/50 px-6 py-4">
                  <DialogTitle className="text-xl font-semibold text-slate-900 dark:text-white">
                    Add Client Contact
                  </DialogTitle>
                  <button
                    onClick={onClose}
                    className="dark:text-dark-300 dark:hover:bg-dark-600 rounded-full p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-500"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <ScrollShadow className="no-scrollbar scrollbar-sm flex-1 space-y-6 overflow-auto p-6">
                  {/* Contact Information */}
                  <div className="space-y-4">
                    <label className="block text-xs font-semibold tracking-wider text-gray-400 uppercase dark:text-gray-500">
                      Contact Information
                    </label>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                          Full Name <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <input
                            type="text"
                            placeholder="John Doe"
                            value={fullName}
                            onChange={(e) => {
                              setFullName(e.target.value);
                              handleSearchContact(
                                "fullName",
                                e.target.value,
                                "name",
                              );
                              if (errors.fullName)
                                setErrors((prev) => ({
                                  ...prev,
                                  fullName: "",
                                }));
                            }}
                            onBlur={() =>
                              setShowSuggestions((prev) => ({
                                ...prev,
                                fullName: false,
                              }))
                            }
                            className={`w-full rounded-lg border ${errors.fullName ? "border-red-500" : "dark:border-dark-600 border-gray-300"} dark:bg-dark-900 focus:border-primary-500 focus:ring-primary-500 bg-white px-3 py-2 text-sm text-gray-900 transition-colors focus:ring-1 focus:outline-none dark:text-white`}
                          />
                          {renderSuggestionsDropdown("fullName")}
                        </div>
                        {errors.fullName && (
                          <p className="mt-1 text-xs text-red-500">
                            {errors.fullName}
                          </p>
                        )}
                      </div>

                      <div className="flex flex-col">
                        <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                          Phone Number 
                        </label>
                        <div className="relative">
                          <div
                            className={clsx(
                              "dark:bg-dark-900 relative flex min-h-[38px] items-center rounded-lg border bg-white transition-all duration-200",
                              errors.phoneNumber
                                ? "border-red-500 focus-within:border-red-500 focus-within:ring-1 focus-within:ring-red-500"
                                : "dark:border-dark-600 focus-within:border-primary-500 focus-within:ring-primary-500 border-gray-300 focus-within:ring-1",
                            )}
                          >
                            <PhoneInput
                              international
                              defaultCountry="AU"
                              value={phoneNumber}
                              countrySelectProps={{ tabIndex: -1 }}
                              countryCallingCodeEditable={false}
                              onChange={(val) => {
                                let cleanVal = val || "";
                                if (cleanVal) {
                                  if (cleanVal.startsWith("+61")) {
                                    const digits = cleanVal.slice(3).replace(/\D/g, "");
                                    if (digits.length > 10) {
                                      cleanVal = `+61${digits.slice(0, 10)}`;
                                    }
                                  } else if (cleanVal.startsWith("+")) {
                                    const match = cleanVal.match(/^(\+\d{1,4})(.*)$/);
                                    if (match) {
                                      const prefix = match[1];
                                      const digits = match[2].replace(/\D/g, "");
                                      if (digits.length > 10) {
                                        cleanVal = `${prefix}${digits.slice(0, 10)}`;
                                      }
                                    }
                                  } else {
                                    const digits = cleanVal.replace(/\D/g, "");
                                    if (digits.length > 10) {
                                      cleanVal = digits.slice(0, 10);
                                    }
                                  }
                                }
                                setPhoneNumber(cleanVal);
                                handleSearchContact("phoneNumber", cleanVal, "phone");
                                setErrors((prev) => ({ ...prev, phoneNumber: false, email: false }));
                              }}
                              onBlur={() => {
                                setShowSuggestions((prev) => ({
                                  ...prev,
                                  phoneNumber: false,
                                }));
                              }}
                              className="react-phone-input w-full px-3 py-2"
                              numberInputProps={{
                                className:
                                  "w-full bg-transparent border-none focus:ring-0 p-0 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none",
                                required: false,
                                maxLength: 15,
                                onFocus: (e) => {
                                  const target = e.target;
                                  setTimeout(() => {
                                    if (target) {
                                      const valLen = target.value.length;
                                      target.setSelectionRange(valLen, valLen);
                                    }
                                  }, 10);
                                },
                                onKeyUp: (e) => {
                                  if (e.key === "Tab") {
                                    const valLen = e.target.value.length;
                                    e.target.setSelectionRange(valLen, valLen);
                                  }
                                },
                              }}
                            />
                          </div>
                          {renderSuggestionsDropdown("phoneNumber")}
                        </div>
                      </div>

                      <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                          Email Address 
                        </label>
                        <div className="relative">
                          <input
                            type="email"
                            placeholder="john@example.com"
                            value={email}
                            onChange={(e) => {
                              setEmail(e.target.value);
                              handleSearchContact(
                                "email",
                                e.target.value,
                                "email",
                              );
                              setErrors((prev) => ({ ...prev, email: false, phoneNumber: false }));
                            }}
                            onBlur={() =>
                              setShowSuggestions((prev) => ({
                                ...prev,
                                email: false,
                              }))
                            }
                            className={`w-full rounded-lg border ${errors.email ? "border-red-500" : "dark:border-dark-600 border-gray-300"} dark:bg-dark-900 focus:border-primary-500 focus:ring-primary-500 bg-white px-3 py-2 text-sm text-gray-900 transition-colors focus:ring-1 focus:outline-none dark:text-white`}
                          />
                          {renderSuggestionsDropdown("email")}
                        </div>
                      </div>

                      <div className="flex flex-col">
                        <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                          Company Name{" "}
                          
                        </label>
                        <CustomCreatableDropdown
                          height="38px"
                          minHeight="38px"
                          options={companyDropdown.options}
                          value={
                            companyName
                              ? companyDropdown.options.find(
                                  (opt) => opt.label === companyName,
                                ) || { label: companyName, value: companyName }
                              : null
                          }
                          onChange={(val) =>
                            setCompanyName(val ? val.label : "")
                          }
                          onCreateOption={async (val) => {
                            const res =
                              await companyDropdown.onCreateOption(val);
                            if (res && res.success) setCompanyName(val);
                            return res;
                          }}
                          onEditOption={async (id, currentLabel, newText) => {
                            const success = await companyDropdown.onEditOption(
                              id,
                              currentLabel,
                              newText,
                            );
                            if (success && companyName === currentLabel)
                              setCompanyName(newText);
                            return success;
                          }}
                          onDeleteOption={async (id, label) => {
                            const success =
                              await companyDropdown.onDeleteOption(id, label);
                            if (success && companyName === label)
                              setCompanyName("");
                            return success;
                          }}
                          isLoading={companyDropdown.isLoading}
                          placeholder="Company Name"
                          error={!!errors.companyName}
                        />
                      </div>
                      <div>
                        <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                          ABN
                        </label>
                        <input
                          type="text"
                          placeholder="ABN"
                          maxLength={14}
                          value={abnNumber}
                          onChange={(e) => setAbnNumber(formatABN(e.target.value))}
                          className={`w-full rounded-lg border ${errors.abnNumber ? "border-red-500" : "dark:border-dark-600 border-gray-300"} dark:bg-dark-900 focus:border-primary-500 focus:ring-primary-500 bg-white px-3 py-2 text-sm text-gray-900 transition-colors focus:ring-1 focus:outline-none dark:text-white`}
                        />
                      </div>
                    </div>

                    <div className="dark:border-dark-700 space-y-4 border-t border-gray-100 pt-2">
                      <label className="block text-xs font-semibold tracking-wider text-gray-400 uppercase dark:text-gray-500">
                        Address Details
                      </label>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="col-span-2">
                          <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                            Street Address
                          </label>
                          <input
                            type="text"
                            placeholder="123 Example St"
                            value={streetAddress}
                            onChange={(e) => setStreetAddress(e.target.value)}
                            className="dark:border-dark-600 dark:bg-dark-900 focus:border-primary-500 focus:ring-primary-500 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 transition-colors focus:ring-1 focus:outline-none dark:text-white"
                          />
                        </div>

                        <div className="col-span-2 grid grid-cols-1 gap-3 md:grid-cols-3">
                          <div className="flex flex-col">
                            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                              Suburb
                            </label>
                            <CustomCreatableDropdown
                              height="38px"
                              minHeight="38px"
                              options={suburbDropdown.options}
                              value={
                                suburb
                                  ? suburbDropdown.options.find(
                                      (opt) => opt.label === suburb,
                                    ) || { label: suburb, value: suburb }
                                  : null
                              }
                              onChange={(val) => {
                                const label = val ? val.label : "";
                                setSuburb(label);
                                lookupSuburbRecommendation(label);
                              }}
                              onCreateOption={async (val) => {
                                const res =
                                  await suburbDropdown.onCreateOption(val);
                                if (res && res.success) setSuburb(val);
                                return res;
                              }}
                              onEditOption={async (
                                id,
                                currentLabel,
                                newText,
                              ) => {
                                const success =
                                  await suburbDropdown.onEditOption(
                                    id,
                                    currentLabel,
                                    newText,
                                  );
                                if (success && suburb === currentLabel)
                                  setSuburb(newText);
                                return success;
                              }}
                              onDeleteOption={async (id, label) => {
                                const success =
                                  await suburbDropdown.onDeleteOption(
                                    id,
                                    label,
                                  );
                                if (success && suburb === label) setSuburb("");
                                return success;
                              }}
                              isLoading={suburbDropdown.isLoading}
                              placeholder="Suburb"
                            />
                          </div>
                          <div className="flex flex-col">
                            <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
                              Postal Code
                            </label>
                            <input
                              type="text"
                              value={postalCode}
                              onChange={(e) => setPostalCode(e.target.value)}
                              className="block h-[38px] w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 transition-colors duration-200 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-dark-600 dark:bg-dark-900 dark:text-slate-100"
                              placeholder="Postal Code"
                            />
                          </div>
                          <div className="flex flex-col">
                            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                              State
                            </label>
                            <CustomCreatableDropdown
                              height="38px"
                              minHeight="38px"
                              options={stateDropdown.options}
                              value={
                                state
                                  ? stateDropdown.options.find(
                                      (opt) => opt.label === state,
                                    ) || { label: state, value: state }
                                  : null
                              }
                              onChange={(val) => setState(val ? val.label : "")}
                              onCreateOption={async (val) => {
                                const res =
                                  await stateDropdown.onCreateOption(val);
                                if (res && res.success) setState(val);
                                return res;
                              }}
                              onEditOption={async (
                                id,
                                currentLabel,
                                newText,
                              ) => {
                                const success =
                                  await stateDropdown.onEditOption(
                                    id,
                                    currentLabel,
                                    newText,
                                  );
                                if (success && state === currentLabel)
                                  setState(newText);
                                return success;
                              }}
                              onDeleteOption={async (id, label) => {
                                const success =
                                  await stateDropdown.onDeleteOption(id, label);
                                if (success && state === label) setState("");
                                return success;
                              }}
                              isLoading={stateDropdown.isLoading}
                              placeholder="State"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </ScrollShadow>

                {/* Footer */}
                <div className="dark:border-dark-600 dark:bg-dark-800/50 flex shrink-0 items-center justify-end gap-3 border-t border-slate-100 bg-slate-50/50 px-6 py-4">
                  <Button
                    variant="flat"
                    onClick={onClose}
                    disabled={submitting}
                    className="h-9 font-bold"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="filled"
                    color="primary"
                    onClick={handleSave}
                    disabled={submitting}
                    className="shadow-primary/20 flex h-9 items-center justify-center gap-2 px-8 font-bold shadow-lg"
                  >
                    {submitting ? (
                      <>
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                        <span>Saving...</span>
                      </>
                    ) : (
                      "Save"
                    )}
                  </Button>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
